/**
 * Password reset (plan 032). Delegates to Firebase Auth's built-in
 * `sendPasswordResetEmail`. The user gets an email from Firebase with a
 * link to a Firebase-hosted page where they pick a new password. Firebase
 * updates the credential; the user comes back to `/login` and signs in.
 *
 * IMPORTANT: resetting the Firebase password does NOT re-wrap the local
 * family key. After a reset, the browser's vault (wrapped under the old
 * password) will fail to unlock — the LoginRoute detects this and routes
 * to Recover-with-mnemonic, which re-wraps under the new password.
 */

import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { friendlyFirebaseError, requestPasswordReset } from '../data/firebase';
import { Button } from '../components/Button';
import { Input } from '../components/Input';

export function ResetRequestRoute() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  );
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(friendlyFirebaseError(err));
    }
  }

  return (
    <main class="mx-auto max-w-xl px-6 py-16">
      <div class="eyebrow mb-6">Reset</div>
      <h1 class="display-italic text-[36px] leading-tight mb-4 max-w-[20ch]">
        Reset your password
      </h1>
      <p class="text-surface-body max-w-[62ch] mb-8">
        We&apos;ll email you a link to pick a new password. Once you sign in
        with it, this browser will prompt you for your 24-word recovery phrase
        &mdash; that&apos;s how we re-encrypt your family data under the new
        password. If you&apos;ve lost the phrase too, your saved data can&apos;t
        be recovered and you&apos;ll need to start a fresh account.
      </p>

      {status === 'sent' ? (
        <div class="border border-line p-6 space-y-4">
          <p class="font-mono text-[11px] tracking-[0.14em] uppercase text-signal">
            Email sent
          </p>
          <p class="text-surface-fg">
            If <span class="font-mono">{email}</span> is registered, an email is
            on the way with a link to pick a new password. Open it, then come
            back to log in.
          </p>
          <div class="pt-2">
            <button
              type="button"
              onClick={() => {
                location.route('/login');
              }}
              class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted hover:text-signal"
            >
              Back to login
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} class="space-y-4 max-w-md" noValidate>
          <label class="block space-y-2">
            <span class="font-mono text-[11px] tracking-[0.08em] uppercase text-surface-muted">
              Email
            </span>
            <Input
              type="email"
              value={email}
              onInput={(e: Event) => {
                setEmail((e.target as HTMLInputElement).value);
              }}
              required
              autocomplete="email"
            />
          </label>
          {error !== null && <p class="text-signal text-[13px]">{error}</p>}
          <div class="flex items-center gap-4">
            <Button
              type="submit"
              disabled={status === 'sending' || email.length === 0}
            >
              {status === 'sending' ? 'Sending…' : 'Send reset email'}
            </Button>
            <button
              type="button"
              onClick={() => {
                location.route('/login');
              }}
              class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted hover:text-signal"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </main>
  );
}
